/**
 * Declarative sandbox test runner. Authors describe timed steps; the runner
 * boots the whole game into a hidden createTestHarness and drives it.
 * See docs/superpowers/specs/2026-05-27-define-sandbox-test-design.md.
 */
import { runVirtualTest } from '@gotti/playground/virtual';
import { TestRunCancelledError, currentRunSignal, describe, it } from './testRuntime';
import { getRealSetTimeout, virtualTimersInstalled } from './virtualClock';
import { runsOnPlayground, sandboxTestToPlayground, type PlaygroundBootOptions } from './playgroundRun';
// The registered api types. A test's `client` side `api` is typed as the
// `ClientApiRegistry` the game's client systems declare (via
// `declare module '@gotti/client' { interface ClientApiRegistry {...} }`), and
// the `server` side `api` as the `ServerApiRegistry`. So `client.act((api) =>
// api.players)` autocompletes the user's own registered api with no manual
// generic args. Type-only imports — no runtime dependency on client/server.
import type { ClientApiRegistry } from '@gotti/client';
import type { ServerApiRegistry } from '@gotti/server';

export type ValidKey = string;

/**
 * Note: the asset assertions (expectAssetLoaded/expectSpriteDrawn) PREPEND a
 * custom `message` to their `[Asset:<path>]`-prefixed diagnostic detail
 * (unlike expectNotBlank, which replaces it) — the diagnostics feed the AI
 * fix loop and must never be suppressed by a custom message.
 */
export interface CanvasProbe {
  expectNotBlank(message?: string): void;
  expectColor(hex: string, opts?: { minPercent?: number; tolerance?: number }): void;
  drawnPixelCount(): number;
  /**
   * Strict surface check — mirrors the production e2e canvas gate exactly:
   * after quantizing each pixel to a 16-bucket-per-channel colour, the canvas
   * must show at least `minColors` distinct colours AND at least
   * `minDrawnPixels` pixels that are NOT the single dominant (background)
   * colour. A flat single-colour fill therefore FAILS — only a surface with a
   * visible, contrasting structure (e.g. a board/grid drawn on top of the
   * background) passes. Use this for the rendering test so "all tests pass"
   * actually entails a non-blank rendered surface.
   */
  expectSurfaceRendered(opts?: { minColors?: number; minDrawnPixels?: number; message?: string }): void;
  /**
   * Asserts the asset at `path` was preloaded into the AssetManager at boot.
   * Failure messages start with `[Asset:<path>]` and list what WAS loaded.
   * Throws `[Asset:<path>] asset probe unavailable in this runner` when the
   * host installed no AssetHost (never silently passes).
   */
  expectAssetLoaded(path: string, message?: string): void;
  /**
   * Asserts a ctx.drawImage call on THIS client's canvas drew the image asset
   * at `path` this run. `frame`: the draw's 9-arg source subrect must equal
   * the named frame's rect in the sheet's AnimationAtlas JSON (resolved via
   * the host; exact sx/sy/sw/sh compare). `near`: at least one matching draw's
   * destination (dx, dy) must be within `tolerance` px (default 1) of (x, y).
   * Failure messages start with `[Asset:<path>]` and describe what WAS drawn.
   */
  expectSpriteDrawn(path: string, opts?: { frame?: string; near?: { x: number; y: number; tolerance?: number }; message?: string }): void;
  /** Raw recorded draws for this canvas (diagnostics). */
  drawnSprites(): Array<{ assetPath: string | null; dx: number; dy: number }>;
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
}

/**
 * A per-test value bag shared across ALL steps and BOTH sides (client + server)
 * of a single test. Capture a value in one step (`store.startPos = …`) and read
 * it in a later step or on the other side. This is the supported way to carry
 * state between steps — there is NO `$api.setTestValue/getTestValue`.
 */
export type SandboxTestStore = Record<string, any>;

/**
 * Meta handle for the client a step targets (per the step's `clientIndex`),
 * passed as the LAST argument to every side callback. Lets a test reference
 * "the client I'm driving" without hardcoding ids or indices, and exposes
 * test-level utilities that live outside the game's own `$api`.
 */
export interface TestClient<Api = any> {
  /** Stable player id of this client — the framework join id (`player-1`, …). */
  id: string;
  /** 0-based client index — matches the step's `clientIndex`. */
  index: number;
  /** This client's merged `$api`. */
  api: Api;
  /** Deliver a keypress to THIS client. */
  press: (key: ValidKey) => void;
  /** The most recent key the test pressed on this client (null if none yet). */
  getPressingKey: () => ValidKey | null;
  /** Every key the test has pressed on this client, in order. */
  getPressedKeys: () => ValidKey[];
  /** Simulate this client disconnecting — same as `client: { disconnect: true }`. */
  disconnect: () => void | Promise<void>;
}

export interface SandboxTestSide<Api = any> {
  /**
   * Key(s) to press at this step — or a CALLBACK that decides at execution
   * time from live state: `(api, store, client) => key | keys | null`. The
   * callback runs when the step fires (before `act`), reads this side's
   * `$api`, the shared `store`, and the target `client` meta handle, and
   * returns the key(s) to press; return `null` / `undefined` to press
   * NOTHING (a conditional passthru — e.g. "steer only if the heading isn't
   * already right", skipping an illegal 180° reversal).
   */
  press?:
    | ValidKey
    | ValidKey[]
    | ((api: Api, store: SandboxTestStore, client: TestClient) => ValidKey | ValidKey[] | null | undefined);
  click?: { x: number; y: number };
  type?: string;
  /**
   * Simulate THIS client disconnecting — the same event the sandbox UI fires
   * when you close a client's canvas window. The server's `onClientLeft`
   * cleanup runs and the client stops ticking. Use it (with `clientIndex`) to
   * test disconnect cleanup, e.g. that a player's entity is removed. Client
   * side only; ignored on the `server` side.
   */
  disconnect?: boolean;
  act?: (api: Api, store: SandboxTestStore, client: TestClient) => void | Promise<void>;
  assert?: (api: Api, canvas: CanvasProbe, store: SandboxTestStore, client: TestClient) => void | Promise<void>;
}

/**
 * Step-level `until` — asserts a TRANSITION (a wrap across an edge, a score
 * threshold, a respawn) on the EXACT tick it happens, instead of a wide fixed
 * `atTime` window that samples long after the moment (the entity keeps moving
 * and the assertion reads stale state). Polled once per tick, server-side,
 * after the step's `atTime` is reached; when `cond` first returns true the
 * step's `client`/`server` sides execute on that SAME tick.
 */
export interface StepUntilOptions<ServerApi = any> {
  /** Polled once per tick (server-side authoritative read) after the step's
   *  `atTime` is reached; the step's sides execute on the SAME tick the
   *  condition first holds. */
  cond: (serverApi: ServerApi, store: SandboxTestStore) => boolean;
  /** Virtual-ms budget measured from the step's `atTime`. Default 30000 —
   *  generous ON PURPOSE: virtual time is free when the condition arrives early
   *  (polling stops immediately), so authors should normally OMIT this. */
  maxMs?: number;
  /** Names the condition in the failure message. */
  label?: string;
}

/**
 * Thrown by a step's `until` when its condition never became true within the
 * virtual-time budget (measured from the step's `atTime`). Message always
 * starts with `'UNTIL FAILED: '`.
 */
export class UntilFailedError extends Error {}

export interface SandboxTestStep<ClientApi = ClientApiRegistry, ServerApi = ServerApiRegistry> {
  atTime: number;
  label: string;
  /**
   * Which client this step's `client` side targets (0-based). Defaults to 0.
   *
   * The harness boots `test.clients` REAL clients with distinct ids
   * (`player-1`, `player-2`, …) — the SAME split-screen clients the sandbox
   * UI's "Add Client" button creates. `clientIndex` selects which one this
   * step drives: its `press`/`click`/`type` input is delivered to ONLY that
   * client (it becomes the focused client for the keypress), its `act` runs
   * against that client's `$api`, and a `client.assert` reads that client's
   * own canvas. The `server` side is always the single shared server.
   */
  clientIndex?: number;
  client?: SandboxTestSide<ClientApi>;
  server?: SandboxTestSide<ServerApi>;
  /**
   * Assert a TRANSITION on the exact tick it happens rather than a wide fixed
   * `atTime`. When present, the runner ticks to `atTime` as usual, then polls
   * `cond(serverApi, store)` once per tick (checking once immediately at
   * `atTime`, BEFORE any further tick) until it returns true or `maxMs`
   * (virtual, measured from `atTime`) elapses — whichever comes first. The
   * moment `cond` holds, this step's `client`/`server` sides fire on that SAME
   * tick (no extra settle-tick is inserted before them — the existing
   * post-side settle logic is unchanged). If the budget runs out first, the
   * test fails loudly with `UntilFailedError` (`UNTIL FAILED: <label>: …`)
   * instead of silently sampling the wrong moment.
   *
   * REBASE: once THIS step's sides + settle + assert finish, every later
   * step's `atTime` is measured from THIS step's completion, not from the
   * original `setup`/boot origin — mirrors how `setup` rebases `atTime: 0` to
   * "right after setup finishes." A test with no `until` steps is unaffected
   * (every `atTime` stays relative to `setup`'s end, byte-identical to today).
   */
  until?: StepUntilOptions<ServerApi>;
}

/**
 * Thrown by `SetupHandle.until()` when its condition never became true
 * within the virtual-time budget. Message always starts with
 * `'SETUP FAILED: '`.
 */
export class SetupFailedError extends Error {}

export interface SetupUntilOptions {
  /** Virtual-time budget in ms. Default 30000 — generous on purpose: virtual
   *  time is free when the condition arrives early; normally omit this. */
  maxMs?: number;
  /** Virtual ms advanced per attempt (default one tick, ~16.667ms). */
  stepMs?: number;
  /** Called each attempt BEFORE ticking — read state, press keys. */
  drive?: () => void;
  /** Names the precondition in the SETUP FAILED message. */
  label?: string;
}

/**
 * Imperative handle passed to `SandboxTest.setup.run`. Lets a test drive the
 * game into a non-trivial starting state (e.g. "snake has grown to length
 * 3") through the SAME gameplay-only input path steps use — no privileged
 * setters — while consuming virtual time on the same clock the declarative
 * `steps` timeline runs on. `steps[].atTime` is relative to when `setup`
 * completes.
 */
export interface SetupHandle<ClientApi = any, ServerApi = any> {
  serverApi: ServerApi;
  clientApi(index?: number): ClientApi;
  clientId(index?: number): string;
  press(key: string, clientIndex?: number): void;
  click(x: number, y: number, clientIndex?: number): void;
  type(text: string, clientIndex?: number): void;
  tick(ms: number): Promise<void>;
  until(cond: () => boolean, opts?: SetupUntilOptions): Promise<void>;
  store: Record<string, unknown>;
}

export interface SandboxTest<ClientApi = ClientApiRegistry, ServerApi = ServerApiRegistry> {
  label: string;
  /**
   * Number of distinct clients to boot for this test (default 1).
   *
   * Set `clients: 2` (etc.) to test multiplayer the way the framework actually
   * runs it — N real split-screen clients, ids `player-1..player-N`, all
   * joined to the one shared server. Each step targets a client via
   * `clientIndex`. Do NOT fake extra players by calling server spawn APIs from
   * a test; declare `clients` and let the framework's join flow
   * (`onClientJoined`) create them. Omitting `clients` keeps the single-client
   * behaviour byte-for-byte unchanged.
   */
  clients?: number;
  /**
   * Simulated client↔server transport latency, in MILLISECONDS (one-way), for
   * THIS test's runtime. Configured here alongside `clients` — same place you
   * size the test's client setup.
   *
   * Default `0` — instant, deterministic delivery: a client action's
   * authoritative effect is visible within a tick or two. This is what
   * behavioral / game-logic tests want, and it is what a test that presses a key
   * then asserts the resulting state a few steps later assumes. Leave it unset
   * for those. The real `ClientProcess`/`ServerProcess`/transport STILL run — this
   * only sets the network delay the transport applies to each message; it does
   * NOT make the runtime any less real.
   *
   * Set a realistic value (e.g. `50`–`150`) to TEST NETCODE UNDER LAG —
   * client-side prediction, server reconciliation, lag compensation. When
   * `latency > 0`, a full client→server→client round-trip takes roughly
   * `2 × latency` plus a couple of ticks, so you MUST space your `atTime` gaps to
   * let an action propagate before you assert its authoritative result: a `press`
   * at `atTime: T` is not authoritative on the server until about
   * `T + latency + one tick`, and not reflected back on the client until about
   * `T + 2×latency`. A test that asserts too soon under lag will (correctly) see
   * the pre-action state, so budget the gaps to the latency you set.
   */
  latency?: number;
  steps: SandboxTestStep<ClientApi, ServerApi>[];
  /**
   * Optional imperative phase that runs after boot + client joins and BEFORE
   * the declarative `steps` timeline. Use it to drive the game — via the
   * same gameplay-only input path steps use — into a non-trivial starting
   * state (e.g. "grow the snake to length 3") deterministically, on the same
   * virtual clock the step timeline consumes. `steps[].atTime` is relative
   * to when `setup` completes (t=0 = setup done); omitting `setup` leaves
   * step timing byte-identical to today.
   */
  setup?: { label: string; run: (h: SetupHandle<ClientApi, ServerApi>) => void | Promise<void> };
  timeScale?: number;
  timeoutMs?: number;
  realtime?: boolean;
  assertionMode?: { runSequentially?: boolean; failFast?: boolean };
}

export interface SandboxTestSuite<ClientApi = ClientApiRegistry, ServerApi = ServerApiRegistry> {
  title: string;
  tests: SandboxTest<ClientApi, ServerApi>[];
}

export interface BootedGame {
  harness: any;
  /** Simulate the client at `clientIndex` disconnecting (UI "close window"). */
  disconnect?: (clientIndex: number) => void | Promise<void>;
  /** Merged `$api` of client index 0 — back-compat alias of `clientApis[0]`. */
  clientApi: any;
  /**
   * One merged client `$api` per booted client, index-aligned with the
   * `clientIndex` used on steps. Single-client boots produce a 1-element array
   * whose [0] === `clientApi`.
   */
  clientApis?: any[];
  serverApi: any;
  /** Canvas of client index 0 — back-compat alias of `canvases[0]`. */
  canvas: HTMLCanvasElement | null;
  /** One canvas per booted client, index-aligned with `clientIndex`. */
  canvases?: (HTMLCanvasElement | null)[];
  /** Player ids per client index. Absent ⇒ the framework join convention
   *  (`player-1`, `player-2`, …) is assumed. */
  clientIds?: string[];
  /** Tears the boot down. May be async (a playground mount disposes asynchronously); the engine awaits it. */
  destroy: () => void | Promise<void>;
}
/**
 * Boots the game into a hidden harness. Receives the declared client count
 * (`test.clients ?? 1`) so it can spin up that many real clients. Existing
 * zero-arg booters keep working — they just ignore the argument and boot one
 * client.
 */
export type SandboxGameBooter = (
  clientCount?: number,
  /** Per-test runtime options. `latencyMs` is the test's `latency` (default 0) —
   *  the simulated one-way client↔server transport latency the real-runtime
   *  booter applies. Older booters that take only `clientCount` ignore it. */
  opts?: { latencyMs?: number },
) => BootedGame | Promise<BootedGame>;

let gameBooter: SandboxGameBooter | null = null;
export function setSandboxGameBooter(booter: SandboxGameBooter | null): void {
  gameBooter = booter;
}
export function getSandboxGameBooter(): SandboxGameBooter | null {
  return gameBooter;
}

export interface InputDriver {
  /** Drive a keypress into the given client (default 0). */
  press: (key: string, clientIndex?: number) => void;
  click: (x: number, y: number, clientIndex?: number) => void;
  type: (text: string, clientIndex?: number) => void;
}
let inputDriver: InputDriver | null = null;
export function setInputDriver(d: InputDriver | null): void { inputDriver = d; }

export function defineSandboxTest<ClientApi = ClientApiRegistry, ServerApi = ServerApiRegistry>(
  suite: SandboxTestSuite<ClientApi, ServerApi>,
): void {
  describe(suite.title, () => {
    for (const t of suite.tests) {
      it(t.label, async () => { await runSandboxTest(t); });
    }
  });
}

export async function runSandboxTest(test: SandboxTest<any, any>): Promise<void> {
  if (!gameBooter) {
    throw new Error(
      'defineSandboxTest: no game booter installed — the sandbox runtime must ' +
      'call setSandboxGameBooter() before runCollectedTests().',
    );
  }
  const clientCount = Math.max(1, test.clients ?? 1);
  // Per-test transport latency (ms). Default 0 = instant/deterministic delivery
  // (behavioral tests); a test sets `latency` to exercise netcode under lag.
  const latencyMs = Math.max(0, test.latency ?? 0);
  // What is the DSL's here — the input driver, the run's cancellation, the DSL's error classes, the booted latency, the
  // wall clock — is handed to the playground's executor, whichever way the booter runs the test. Read when the steps
  // start, after the boot: a booter installs its own input driver as it boots.
  const executorOptions = () => ({
    input: inputDriver,
    cancelled: () => currentRunSignal()?.aborted === true,
    cancelledError: () => new TestRunCancelledError(),
    latencyMs,
    untilFailed: (message: string) => new UntilFailedError(message),
    setupFailed: (message: string) => new SetupFailedError(message),
    realDelay,
  });
  // A booter that mounts the test with the game runs it through that mount (`runTest`), on the playground's runner: the
  // executor runs the test's steps there, and the game is torn down by the booter once the run is over — a body that
  // disposed its own mount would wait on itself.
  let game: Awaited<ReturnType<SandboxGameBooter>> | undefined;
  const bootOptions: PlaygroundBootOptions = {
    latencyMs,
    test: sandboxTestToPlayground(test, {
      clockInstalled: virtualTimersInstalled,
      run: ({ onEvent }) => runVirtualTest(test, { ...game!, destroy: () => undefined }, { ...executorOptions(), onEvent }),
    }),
  };
  // The booter MAY be async (the real-runtime booter boots + joins under the
  // virtual clock); the harness booter returns synchronously. Awaiting both is
  // safe — `await` on a non-Promise resolves immediately.
  game = await gameBooter(clientCount, bootOptions);
  // Guard a malformed test (e.g. a truncated/incomplete generated spec whose
  // last entry has no `steps`) so it fails with a clear message instead of a
  // cryptic "steps is not iterable" that propagates into failure classification.
  if (!Array.isArray(test.steps)) {
    throw new Error(
      `malformed test "${test.label ?? '(unnamed)'}": "steps" must be an array (got ` +
        `${test.steps === undefined ? 'undefined' : typeof test.steps}) — the test file may be truncated or incomplete.`,
    );
  }
  if (runsOnPlayground(game)) {
    const result = await game.runTest();
    // What the body threw is the test's failure, unchanged — the same object the direct path throws.
    if ('thrown' in result) throw result.thrown;
    if (!result.success) throw new Error(result.errors.map((error) => error.message).join('\n'));
    return;
  }
  // A booter that only boots (the harness booters): the executor runs the steps against its game and tears it down.
  await runVirtualTest(test, game, executorOptions());
}

/**
 * A WALL-CLOCK delay for `realtime: true` watchability — it must use the REAL
 * `setTimeout`, never the virtual clock's. The real-runtime path installs the
 * virtual clock (which replaces global `setTimeout` with one that only fires when
 * the test driver calls `advance()`); a `realtime` step's per-tick delay runs
 * INSIDE the tick loop (it is NOT an advance), so a virtual `setTimeout(r, ms)`
 * here would never fire — the test would deadlock and time out. `getRealSetTimeout()`
 * returns the genuine wall-clock timer (the saved real one when the virtual clock
 * is installed, the live global otherwise), so the delay always elapses for real.
 */
function realDelay(ms: number): Promise<void> {
  const realSetTimeout = getRealSetTimeout();
  return new Promise(r => realSetTimeout(r, ms));
}
